import { dehydrate, QueryClient } from "@tanstack/react-query";
import { GetStaticPropsContext } from "next/types";
import { BreadcrumbJsonLd } from "next-seo";
import ArticleSeo from "@/components/article/ArticleSeo";
import ArticleContainer from "@/components/container/ArticleContainer";
import ArticlePreviewContainer from "@/components/container/ArticlePreviewContainer";
import SportContainer from "@/components/container/SportContainer";
import PreviewSuspense from "@/components/preview/PreviewSuspense";
import SportSeo from "@/components/sport/SportSeo";
import {
  Article,
  GenericSport,
  StatusSelector,
  GetRedirectQuery,
  GetSupportedSportsQuery,
  GetArticleBySlugQuery,
  Promotion,
  GetPromotionsQuery,
  GetSingleSportBetsQueryQuery,
} from "@/graphql/gql/graphql";
import { BreadcrumbSegment } from "@/types/breadcrumbs";
import { ArticleList } from "@/types/common";
import {
  getAllArticles,
  getArticleBySlug,
  getAvailableSports,
  getFeaturedArticlesBySport,
  getPromotions,
  getRedirect,
  getSingleSportBets,
} from "@/utils/api";
import { isArticleNotFoundError } from "@/utils/errors";
import { useBreadcrumbs } from "@/utils/hooks/useBreadcrumbs";
import logger from "@/utils/logger";
import { resolveCmsRedirectResponse } from "@/utils/redirects";
import { loadHeader, loadRecentArticles, loadSports } from "@/utils/ssr";
import { filterEmpty, handlePromise, findSportIfSportSlug } from "../utils";

export type SingleSportBets = NonNullable<
  GetSingleSportBetsQueryQuery["getSupportedSports"]
>[number];
interface DynamicProps {
  article?: Article;
  sport?: GenericSport;
  featuredArticles?: Article[];
  preview?: boolean;
  sportName?: string;
  promotions?: Promotion[];
  featuredBets?: SingleSportBets;
}

const DynamicPage = ({
  article,
  sport,
  featuredArticles,
  preview,
  sportName,
  promotions,
  featuredBets,
}: DynamicProps) => {
  const breadcrumbSegments: BreadcrumbSegment[] = [];
  if (sport) {
    breadcrumbSegments.push({
      name: sport.name.toUpperCase(),
      path: `/${sport.slug}`,
    });
  }
  const { trail, itemListElements } = useBreadcrumbs(breadcrumbSegments);

  if (featuredArticles && sport) {
    return (
      <>
        <SportSeo sport={sport} />
        <BreadcrumbJsonLd itemListElements={itemListElements} />
        <SportContainer
          featuredBets={featuredBets}
          featuredArticles={featuredArticles}
          sport={sport}
          trail={trail}
        />
      </>
    );
  }

  if (article && sportName) {
    return preview ? (
      <PreviewSuspense>
        <ArticlePreviewContainer article={article} />
      </PreviewSuspense>
    ) : (
      <>
        <ArticleSeo article={article} />
        <ArticleContainer article={article} promotions={promotions} />
      </>
    );
  }
  return <div />;
};

export async function getServerSideProps({
  params,
  preview = false,
}: GetStaticPropsContext) {
  const isPreview = !!preview;
  // one request id per page render, attached as x-request-id on every
  // graphql request made below and included in error logs for tracing
  const requestId = crypto.randomUUID();
  const queryClient = new QueryClient();
  await Promise.all([loadHeader(queryClient), loadSports(queryClient)]).catch(
    logger?.error
  );
  if (!params?.slug) {
    return { notFound: true };
  }

  // Redirect to lowercase destination
  const pageSlug = params?.slug?.toString() ?? "";
  const pageSlugLowerCase = pageSlug.toLocaleLowerCase();
  if (pageSlug !== pageSlugLowerCase) {
    return {
      redirect: {
        destination: `/${pageSlugLowerCase}`,
        permanent: true,
      },
    };
  }

  // Check if slug is a selected sport slug or an article slug
  const [sportsSettled, articleSettled, redirectSettled] =
    await Promise.allSettled([
      getAvailableSports({ isPreview }, requestId),
      getArticleBySlug(
        {
          slug: params?.slug?.toString(),
          contentStatus: isPreview
            ? StatusSelector.Both
            : StatusSelector.Published,
          isPreview,
        },
        requestId
      ),
      getRedirect(params?.slug?.toString(), requestId),
    ]);
  const redirectResponse = handlePromise({
    settled: redirectSettled,
    defaultValue: undefined as unknown as GetRedirectQuery,
  });
  const cmsRedirectResponse = resolveCmsRedirectResponse(
    redirectResponse?.getRedirect,
    `/${pageSlugLowerCase}`
  );
  if (cmsRedirectResponse) {
    return {
      redirect: cmsRedirectResponse,
    };
  }

  const supportedSportsResponse = handlePromise({
    settled: sportsSettled,
    defaultValue: {} as GetSupportedSportsQuery,
    errorMessage: `Slug page getAvailableSports error (requestId: ${requestId})`,
  });
  const supportedSports = supportedSportsResponse.getSupportedSports;
  const selectedSport = findSportIfSportSlug(
    supportedSports,
    pageSlugLowerCase
  );

  // If slug refers to a sport, get featured articles
  if (selectedSport) {
    let featuredArticles: ArticleList = [];
    let featuredBets: SingleSportBets = {};
    try {
      try {
        const [[featuredArticlesResponse], featuredBetsResponse] =
          await Promise.all([
            getFeaturedArticlesBySport([selectedSport.slug], requestId),
            getSingleSportBets({ slug: selectedSport.slug }, requestId),
          ]);

        featuredArticles = featuredArticlesResponse.getFeaturedArticles || [];

        if (featuredBetsResponse.getSupportedSports) {
          featuredBets = featuredBetsResponse.getSupportedSports[0];
        }
      } catch (err) {
        const allArticles = await getAllArticles(
          {
            sportName: selectedSport.name,
          },
          requestId
        );
        featuredArticles = filterEmpty(
          allArticles.getArticles?.articles.map((article) => article?.entity) ||
            []
        ) as ArticleList;
        logger?.error(
          `Error fetching bets and articles for ${selectedSport.name} (requestId: ${requestId})`,
          err
        );
      }
      const moreArticlesClient = await loadRecentArticles(queryClient, {
        limit: 12,
        sportName: selectedSport.name,
        hideDeprioritizedContent: true,
      });
      return {
        props: {
          dehydratedState: dehydrate(moreArticlesClient),
          sport: selectedSport,
          featuredArticles: filterEmpty(featuredArticles || []),
          featuredBets: featuredBets,
        },
      };
    } catch (err) {
      logger?.error(
        `Error: [${params?.slug?.toString()}] is selected sport (requestId: ${requestId})`,
        err
      );
      throw err;
    }
  }

  // If slug refers to an article, get the article
  const articleResponse = handlePromise({
    settled: articleSettled,
    defaultValue: {} as GetArticleBySlugQuery,
    errorMessage: `Slug page getArticleBySlug error (requestId: ${requestId})`,
    // A genuine "no published article" rejection is a 404 (handled below via
    // the empty-article check); any other failure (upstream/network error)
    // should surface as a 500.
    rethrow: (reason) => !isArticleNotFoundError(reason),
  });

  try {
    const article = articleResponse.getSingleArticle;
    if (!article) {
      logger.info("No article found", { slug: params?.slug?.toString() });
      return { notFound: true };
    }
    // If we are in preview mode, the article may not have required fields.
    // In case the article is WIP, ensure all fields at least have default values
    if (preview) {
      const defaultSport = { name: "" };
      const defaultDate = "";
      const defaultAuthor = {
        id: "",
        name: "",
        thumbnailUrl: null,
        socialMedia: null,
      };

      article.title = article.title || "";
      article.firstPublishedAt = article.firstPublishedAt || defaultDate;
      article.body = article.body || "[]";
      article.author = article.author || defaultAuthor;
      article.sport = article.sport || defaultSport;
      article.tags = article.tags || undefined;
    }
    let promotions = [] as GetPromotionsQuery["getPromotions"];
    try {
      const [promotionsData] = await Promise.all([
        getPromotions({ tags: article.tags }, requestId),
        loadRecentArticles(queryClient, { limit: 6 }),
      ]);
      promotions = promotionsData?.getPromotions || [];
    } catch (e) {
      logger?.error(`getPromotions error (requestId: ${requestId})`, e);
    }
    return {
      props: {
        dehydratedState: dehydrate(queryClient),
        article,
        ...params,
        sportName: article.sport.name,
        preview,
        promotions,
      },
    };
  } catch (err) {
    logger.error(
      `Error: [${params?.slug?.toString()}] is article. Preview is ${preview} (requestId: ${requestId})`,
      err
    );
    throw err;
  }
}

export default DynamicPage;
